/* DashboardGrid — ClickFunnels-style 4-column icon grid.
   Big icon + bold underlined title + one-line description per tool.
   This IS the new dashboard centerpiece. Every tile points to a real,
   verified route. Admin-only tiles are gated by `isAdmin`. */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Filter, Globe, Play, Send,
  MessageCircle, BookOpenText, Users, ShoppingBag,
  PenLine, Calendar, Contact, BarChart3,
  Camera, BookOpen, Image as ImageIcon, Inbox,
  Store, ExternalLink, GraduationCap, LifeBuoy,
  Wallet, Video, Award, Sparkles, Link2,
  ArrowRight, X,
} from "lucide-react";

const ORANGE = "#F39A1F";

const BRAND = "#287F8E";   // baby-blue brand accent
const GREEN = "#2E9F3A";
const WINE  = "#8B2A2A";

const MEMBER_TILES = [
  // ─── Row 1 · MAKE MONEY ─────────────────────────────────────────────
  { Icon: ShoppingBag,       title: "Market",              sub: "14 affiliate programs ready to promote today",                       to: "/market" },
  { Icon: ExternalLink,      title: "Affiliate Programs",  sub: "Hand-picked programs Mike has vetted himself",                       to: "/dashboard/affiliate-programs" },
  { Icon: Store,             title: "My Products",         sub: "List your own product on the Faitharama marketplace",                to: "/dashboard/my-products" },
  { Icon: Wallet,            title: "Earnings",            sub: "$24.50/mo on every Pro you refer · $39.50/mo on every Unlimited. Every month. Forever.", to: "/dashboard/affiliates" },

  // ─── Row 2 · BUILD ──────────────────────────────────────────────────
  { Icon: Globe,             title: "Your Mirror Site",    sub: "ONE click-to-edit page at faitharama.com/m/you. Drag, type, save.",   to: "/dashboard/my-mirrors" },
  { Icon: Filter,            title: "Funnel Templates",    sub: "Pre-built starter funnels (Goli, coaching, ebook). Pick one, edit, go.", to: "/dashboard/mirror-site" },
  { Icon: BookOpen,          title: "★ BibleProject — FREE",  sub: "Brought to you by Faitharama. 200+ Bible videos. 100% free, forever.",  to: "/bibleproject", logoSrc: "/logos/bibleproject.png" },
  { Icon: BookOpen,          title: "AI Writer",           sub: "Full-page Claude Sonnet 4.5 writer for sales copy & emails",         to: "/dashboard/ai-writer" },
  { Icon: Send,              title: "Email Engine",        sub: "Reach your list with broadcasts and automated welcome series",       to: "/dashboard/emails" },

  // ─── Row 3 · CONNECT ────────────────────────────────────────────────
  { Icon: Contact,           title: "Contacts",            sub: "Manage your full subscriber list and customer roster",               to: "/dashboard/contacts" },
  { Icon: Inbox,             title: "Sign-Ups",            sub: "Every lead, opt-in, and free-book download in one inbox",            to: "/dashboard/leads" },
  { Icon: PenLine,           title: "Pro Notes",           sub: "Quick notes, prayer journal, and idea pad — synced everywhere",      to: "/dashboard/notes" },
  { Icon: Link2,             title: "Link Rebrander",      sub: "Turn any ugly affiliate URL into a clean faitharama.com/r/ link",    to: "/dashboard/links" },
  { Icon: Calendar,          title: "Appointments",        sub: "Let people book a call with you straight from your mirror site",     to: "/dashboard/appointments" },

  // ─── Row 4 · TRAIN ──────────────────────────────────────────────────
  { Icon: GraduationCap,     title: "Academy",             sub: "How To Use Faitharama — six plain-English starter courses",          to: "/dashboard/academy" },
  { Icon: MessageCircle,     title: "Ask Mike",            sub: "Direct AI mentor trained on Mike's voice and playbook",              to: "/dashboard/ask-mike" },
  { Icon: Play,              title: "My Courses",          sub: "Continue any training course you've started",                        to: "/dashboard/my-courses" },

  // ─── Row 5 · EXTRAS ─────────────────────────────────────────────────
  { Icon: BookOpen,          title: "Library",             sub: "Your live Heyzine bookshelf — every flipbook in one place",          to: "/library" },
  { Icon: ImageIcon,         title: "Book Cover",          sub: "Order a custom book cover from Mike's design service",               to: "/dashboard/book-cover" },
  { Icon: Camera,            title: "Free Photos",         sub: "Royalty-free Pexels & Unsplash images for your posts",               to: "/dashboard/free-photos" },
  { Icon: LifeBuoy,          title: "Help Me, Mike!",      sub: "Direct line to Mike — answers within 24 hours",                      to: "/dashboard/helpdesk" },
];

const ADMIN_TILES = [
  { Icon: Award,             title: "Vendor Sales Ledger", sub: "50/50 commission ledger for every referred sale",                    to: "/dashboard/vendor-sales",        badge: "ADMIN" },
  { Icon: Video,             title: "Walk-Through Videos", sub: "Record and assign welcome videos to dashboard slots",                to: "/admin/tutorials",               badge: "ADMIN", external: true },
  { Icon: GraduationCap,     title: "Course Builder",      sub: "Build training courses members can enroll in",                       to: "/dashboard/course-builder",      badge: "ADMIN" },
  { Icon: ShoppingBag,       title: "Featured Products",   sub: "Pick which products show on the homepage market",                    to: "/dashboard/featured-products",   badge: "ADMIN" },
  { Icon: Users,             title: "Affiliate Manager",   sub: "Review members, approve payouts, manage commissions",                to: "/dashboard/affiliate-manager",   badge: "ADMIN" },
  { Icon: Inbox,             title: "Prayer Inbox",        sub: "Every prayer request members have sent in",                          to: "/dashboard/prayer-inbox",        badge: "ADMIN" },
  { Icon: BarChart3,         title: "Recent Signups",      sub: "Last members through the door, with credit attribution",             to: "/dashboard/attribution",         badge: "ADMIN" },
  { Icon: Sparkles,          title: "Template Builder",    sub: "Author landing-page templates the members can clone",                to: "/dashboard/template-builder",    badge: "ADMIN" },
];

function Tile({ Icon, title, sub, to, badge, external, logoSrc }) {
  const testid = `grid-tile-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const inner = (
    <>
      <div className="relative mb-3">
        {badge && (
          <span className="absolute -top-2 -left-3 text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded text-white"
                style={{ background: WINE }}>{badge}</span>
        )}
        {logoSrc
          ? <img src={logoSrc} alt={title} className="w-14 h-14 object-contain"/>
          : <Icon className="w-14 h-14" strokeWidth={1.5} style={{ color: BRAND }} />
        }
      </div>
      <div className="text-base md:text-lg font-bold underline decoration-2 underline-offset-4 group-hover:no-underline"
           style={{ color: "#0F172A", textDecorationColor: BRAND }}>
        {title}
      </div>
      <div className="text-xs md:text-sm text-foreground/65 mt-1.5 leading-snug max-w-[200px]">
        {sub}
      </div>
    </>
  );
  const cls = "group flex flex-col items-center text-center px-3 py-2 hover:scale-[1.03] transition";
  return external
    ? <a key={title} href={to} data-testid={testid} className={cls}>{inner}</a>
    : <Link key={title} to={to} data-testid={testid} className={cls}>{inner}</Link>;
}

/* ────────────────────────────────────────────────────────────────────
   START-HERE BANNER — explains the 3 steps a brand-new member follows.
   Dismissible (saved per-browser). Shows actual illustrations so people
   don't have to guess what each section does.
   ────────────────────────────────────────────────────────────────── */
function StartHereBanner() {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    setClosed(localStorage.getItem("fa_start_here_closed") === "1");
  }, []);
  if (closed) return null;

  const STEPS = [
    {
      n: "1",
      color: BRAND,
      img: "/onboarding/step1-build.png",
      title: "START HERE — Build Your Page",
      body: "ONE click-to-edit page at faitharama.com/m/you. Drag in a headline, a video, an order button. Save. Done.",
      to: "/dashboard/my-mirrors",
      cta: "Start Building",
    },
    {
      n: "2",
      color: GREEN,
      img: "/onboarding/step2-pick.png",
      title: "Pick Products To Promote",
      body: "Walk the Market. Pick any program (Goli, Amadi Jewelry, Heyzine, more). Grab your share-link. Drop it on your mirror.",
      to: "/market",
      cta: "Open The Market",
    },
    {
      n: "3",
      color: ORANGE,
      img: "/onboarding/step3-earn.png",
      title: "Share Your Link & Get Paid",
      body: "Share your mirror site link. Visitors who buy → you earn HALF of whatever Faitharama earns on that sale. So if a brand pays us 10%, you get 5%. Forever. Stats live in Earnings.",
      to: "/dashboard/affiliates",
      cta: "See My Earnings",
    },
  ];

  const close = () => { localStorage.setItem("fa_start_here_closed", "1"); setClosed(true); };

  return (
    <div className="rounded-2xl border-2 shadow-md p-5 md:p-7 mb-10 relative"
         style={{ background: "#EAF6FC", borderColor: BRAND }}
         data-testid="start-here-banner">
      <button onClick={close} title="Dismiss"
              data-testid="start-here-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/60"
              style={{ color: "#0F172A" }}>
        <X className="w-4 h-4"/>
      </button>
      <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: ORANGE }}>
        ★ FIRST TIME HERE? READ THIS
      </div>
      <h2 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: "#0F172A" }}>
        Three Steps. That&apos;s The Whole Deal.
      </h2>
      <p className="text-sm md:text-base text-foreground/80 mt-2 max-w-3xl">
        Faitharama is a workshop, not a maze. Build a page. Pick what to sell. Share your link. Every sale pays you 50%.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {STEPS.map((s) => (
          <Link key={s.n} to={s.to} data-testid={`start-step-${s.n}`}
                className="group bg-white rounded-xl border-2 overflow-hidden hover:shadow-lg transition flex flex-col"
                style={{ borderColor: `${s.color}55` }}>
            <div className="relative">
              <img src={s.img} alt="" className="w-full h-36 object-cover" loading="lazy"/>
              <div className="absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-base"
                   style={{ background: s.color }}>
                {s.n}
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="font-serif-display text-xl md:text-2xl uppercase leading-[1.05] mb-2 tracking-tight" style={{ color: s.color, fontWeight: 900 }}>
                {s.title}
              </div>
              <p className="text-sm text-foreground/75 mb-3 flex-1">{s.body}</p>
              <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold"
                   style={{ color: s.color }}>
                {s.cta} <ArrowRight className="w-3.5 h-3.5"/>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-5 text-[11px] text-foreground/60 text-center">
        Hide this card with the × in the top-right. You can re-show by clearing browser data.
      </div>
    </div>
  );
}

export default function DashboardGrid() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  return (
    <section className="bg-white rounded-2xl border border-border shadow-sm py-10 px-4 md:px-8" data-testid="dashboard-grid">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: BRAND }}>
            ★ FAITHARAMA HEADQUARTERS ★
          </div>
          <h1 className="font-serif-display text-3xl md:text-5xl uppercase" style={{ color: "#0F172A" }}>
            One Roof. Every Tool You Need.
          </h1>
          <p className="text-base md:text-lg text-foreground/70 max-w-2xl mx-auto mt-3">
            Tap any tile to open the tool. Same look as platforms charging $297/mo — same power, your price.
          </p>
        </div>

        <StartHereBanner />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-10 gap-x-6">
          {MEMBER_TILES.map((t) => <Tile key={t.title} {...t} />)}
        </div>

        {isAdmin && (
          <>
            <div className="my-12 flex items-center gap-4">
              <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${WINE}, transparent)` }} />
              <div className="text-[11px] tracking-[0.32em] uppercase font-bold" style={{ color: WINE }}>
                ★ MIKE&apos;S OFFICE · ADMIN ONLY ★
              </div>
              <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${WINE}, transparent)` }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-10 gap-x-6" data-testid="dashboard-grid-admin">
              {ADMIN_TILES.map((t) => <Tile key={t.title} {...t} />)}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
